<!--
  IssueDecisionPage — /issues/:issueId/ — the member's issue detail.

  Every entry point lands here on the SAME card (UX): the notification deep-link
  (NotificationType::ItemIssueNeedsDecision → /issues/{item_issue_id}/), the
  "قرارات مطلوبة" inbox row, the past-decisions row, and the per-item
  "قرارك مطلوب" badge.

  Since interaction-v2 this is no longer decision-only. The backend's `show`
  serves an issue in EVERY status, so the page renders three shapes off one fetch:
  a live answer form (first answer), the same form as a REVISION (already
  answered, leader hasn't applied), and a read-only detail (terminal, or the
  leader's court with no answer yet). All three show the full `timeline` — the
  member can always see what happened, not just what's asked of them.

  REFRESH: this surface has no realtime push, so the issue is re-read on page
  re-entry and pull-to-refresh; a leader's apply/defer/OOS then lands as a new
  terminal timeline step and flips the page read-only. Re-entry after ANSWERING
  refreshes too — that is what picks up the leader acting on what she just sent.

  ANSWERING NO LONGER CROSSES A SCREEN. The card flips in place and confirms
  inline (the submit button is the moment of confirmation), so the whole
  'resolved' branch this page used to render is gone. ResolvedState is still
  here for `already-handled`, which genuinely IS a different screen.

  And when another decision is waiting, the confirmation offers it: the PAGE
  owns that fetch — it is the only layer allowed to read a list — and hands the
  card a link through its `next-decision` slot. See `loadNextDecision` below.

  ROUTE INVARIANTS (playbook Section 5, mirrors SupplierBoxDetailPage / OrderBoxTotalsPage):
   - EAGER `component:` in routes.js — a push / PWA cold-launch deep-link can make
     this the boot URL; `asyncComponent:` would paint "[object Promise]" (row 16).
   - Guarded by requireMembership; an unauthenticated cold hard-load is bounced to
     /login/ by App.vue's EAGER_MEMBER_REQUIRED_PREFIXES pre-empt BEFORE mount
     (row 22 — eager component rendering an <f7-page> skips beforeEnter on the
     initial URL).
   - `meta.hideTabbar: true` — one decision owns the viewport.

  The whole surface is dark until the backend feature flag flips: a fetch then
  404/403s and lands on the calm "already handled" state — never a raw error.

  `:page-content="false"` on <f7-page>: the page brings its OWN <f7-page-content>
  (for pull-to-refresh). Without the prop <f7-page> wraps that one in a second
  .page-content and both get the navbar's padding, so everything starts one
  navbar-height too low (a blank band under the navbar; measured 76px).
-->
<template>
  <f7-page name="issue-decision" :page-content="false" @page:beforein="onBeforeIn">
    <f7-navbar :title="t('collection.inbox.header')" no-hairline>
      <template #left>
        <f7-link class="issue-decision__back" @click="leave">
          <f7-icon f7="chevron_right" size="22"></f7-icon>
          <span class="issue-decision__back-label">{{ t('general.back') }}</span>
        </f7-link>
      </template>
    </f7-navbar>

    <f7-page-content ptr ptr-mousewheel @ptr:refresh="onPtr">
      <div class="issue-decision__wrap">
        <!-- LOADING — card-shaped skeleton. -->
        <div v-if="issue.state.value === 'loading'" class="issue-skeleton" aria-hidden="true">
          <div class="issue-skeleton__head">
            <f7-skeleton-block class="issue-skeleton__thumb skeleton-effect-wave"></f7-skeleton-block>
            <div class="issue-skeleton__ident">
              <f7-skeleton-block class="issue-skeleton__line issue-skeleton__line--name skeleton-effect-wave"></f7-skeleton-block>
              <f7-skeleton-block class="issue-skeleton__line issue-skeleton__line--pill skeleton-effect-wave"></f7-skeleton-block>
            </div>
          </div>
          <f7-skeleton-block class="issue-skeleton__situation skeleton-effect-wave"></f7-skeleton-block>
          <f7-skeleton-block class="issue-skeleton__body skeleton-effect-wave"></f7-skeleton-block>
          <f7-skeleton-block class="issue-skeleton__btn skeleton-effect-wave"></f7-skeleton-block>
          <f7-skeleton-block class="issue-skeleton__btn issue-skeleton__btn--ghost skeleton-effect-wave"></f7-skeleton-block>
        </div>

        <!-- DETAIL — the answer form (first answer OR revision) and the read-only
             view share ONE branch on purpose: same card, same timeline, only
             `readOnly` differs. Keeping them together is what guarantees the
             detail + history are identical whichever state the member arrives in.

             `isDeciding` spans awaiting+submitting so a submit never remounts the
             card and resets the member's transient selection (chosen variant /
             typed supplier / typed note) — see useItemIssue. -->
        <div
          v-else-if="issue.isDeciding.value || isReadOnly"
          ref="cardHost"
          class="issue-decision__card-host"
          tabindex="-1"
          role="group"
          :aria-label="cardAriaLabel"
        >
          <!-- Why there is nothing to do here. Not a ResolvedState: this is a
               detail screen the member deliberately opened, not an end-state, so
               it explains and then gets out of the way of the history below. -->
          <div v-if="isReadOnly" class="issue-decision__banner" :class="readOnlyBannerClass">
            <f7-icon
              :f7="readOnlyBannerIcon"
              size="22"
              class="issue-decision__banner-icon"
            ></f7-icon>
            <div class="issue-decision__banner-body">
              <p class="issue-decision__banner-title">{{ t(issue.readOnlyTitleKey.value) }}</p>
              <p class="issue-decision__banner-msg">{{ t(issue.readOnlyBodyKey.value) }}</p>
              <!-- What actually happened, read off the terminal timeline step. -->
              <p v-if="issue.outcomeKey.value" class="issue-decision__banner-outcome">
                {{ t(issue.outcomeKey.value) }}
              </p>
            </div>
          </div>

          <issue-card
            :item-name="issue.itemName.value"
            :avatar-url="avatarUrl"
            :item="issue.orderBoxItem.value"
            :question="issue.question.value"
            :photo-url="issue.photoUrl.value"
            :options="issue.options.value"
            :allowed-responses="issue.allowedResponses.value"
            :issue-status="issue.issueStatus.value"
            :is-collected="issue.isCollected.value"
            :submitting="issue.state.value === 'submitting'"
            :respond-error="issue.respondError.value"
            :actions-locked="issue.actionsLocked.value"
            :round-reported-closed="issue.roundReportedClosed.value"
            :member-response="issue.memberResponse.value"
            :member-comment="issue.memberComment.value"
            :member-option-index="issue.memberOptionIndex.value"
            :member-photo-url="issue.memberPhotoUrl.value"
            :member-supplier-name="issue.memberSupplierName.value"
            :box="issue.orderBox.value"
            :response-channel="issue.responseChannel.value"
            :read-only="isReadOnly"
            :pill-variant="issue.pillVariant.value"
            :just-submitted="issue.justSubmitted.value"
            :confirm-message="confirmMessage"
            :can-cancel-out-of-stock="issue.canCancelOutOfStock.value"
            :cancelling="issue.cancellingOutOfStock.value"
            @respond="onRespond"
            @edit="issue.clearJustSubmitted()"
            @suggest-supplier="openSuggestSheet"
            @cancel-out-of-stock="confirmCancelOutOfStock"
          >
            <!-- The next decision, appended to the inline confirmation once the
                 list lands. PROGRESSIVE on purpose: the confirmation paints
                 from the POST response and this arrives after, so a slow or
                 failed fetch degrades to "no link" rather than to a reserved
                 gap or a dead affordance. -->
            <template #next-decision>
              <button
                v-if="nextIssueId"
                type="button"
                class="issue-decision__next"
                @click="goToNextDecision"
              >
                {{ t('collection.answer.nextDecision') }}
              </button>
            </template>
          </issue-card>

          <!-- The full member-safe history — shown in every readable state. -->
          <issue-timeline :steps="issue.timeline.value"></issue-timeline>

          <!-- Her fourth reply lands here. It is the SAME sheet the order line
               offers, and it posts to the same action — so a suggestion made
               from a question and one made from a row are one thing to the
               admin, not two. -->
          <!-- Her fourth reply lands here — and since 2026-09-15 it is an
               ANSWER, not a tip. `mode="answer"` makes the sheet a roster
               picker that POSTS NOTHING: it emits the shop, and this page
               sends it through `respond` with `response: 'wait'`, the same
               action the three radios above use (brief
               2026-09-15-the-shop-rides-the-answer, owner answer H-G).

               That is the whole change. The tip door used to be posted here
               and the backend quietly recorded an answer instead — a
               transitional branch that now has no caller. -->
          <supplier-suggestion-sheet
            v-model:opened="suggestSheetOpen"
            :mode="suggestMode"
            :item="issue.orderBoxItem.value"
            :suppliers="suppliers"
            :reward-cents="rewardCents"
            :loading="suppliersLoading"
            :failed="suppliersFailed"
            :seed-supplier-id="issue.memberSupplierId.value"
            :seed-note="issue.memberComment.value ?? ''"
            @pick="onSupplierPicked"
            @submitted="onSuggestionSubmitted"
            @stale="onSuggestionStale"
          ></supplier-suggestion-sheet>
        </div>

        <!-- ALREADY-HANDLED — leader resolved it / round moved on. Calm, not an
             error. The ONLY remaining ResolvedState on this page: the answer
             path now confirms inline on the card above. -->
        <resolved-state
          v-else-if="issue.state.value === 'already-handled'"
          tone="calm"
          icon="checkmark_seal_fill"
          :title="t('collection.resolvedElsewhere.title')"
          :message="t('collection.resolvedElsewhere.body')"
          @done="done"
        ></resolved-state>

        <!-- ERROR — load failed (network/5xx). Retryable, never a dead end. -->
        <div v-else class="issue-decision__error">
          <f7-icon f7="wifi_exclamationmark" size="46" color="orange"></f7-icon>
          <p class="issue-decision__error-msg">{{ t('common.loadError') }}</p>
          <f7-button large outline class="issue-decision__retry" @click="issue.retry()">
            {{ t('general.retry') }}
          </f7-button>
        </div>
      </div>
    </f7-page-content>
  </f7-page>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import IssueCard from '@/components/collection/IssueCard.vue';
import IssueTimeline from '@/components/collection/IssueTimeline.vue';
import ResolvedState from '@/components/collection/ResolvedState.vue';
import SupplierSuggestionSheet from '@/components/orders/SupplierSuggestionSheet.vue';
import { useSupplierList } from '@/composables/commerce/useSupplierList.js';
import { useDecisionsInbox } from '@/composables/useDecisionsInbox.js';
import { ISSUES_INBOX_ROUTE, issueRoute } from '@/composables/useIssueNavigation.js';
import { useItemIssue } from '@/composables/useItemIssue.js';
import { useToast } from '@/composables/useToast.js';
import { MEMBER_RESPONSE, pillLabelKey } from '@/constants/collectionIssue.js';

// F7-Vue passes f7route / f7router as props to `component:` routes.
const props = defineProps({
  f7route: { type: Object, required: true },
  f7router: { type: Object, default: null },
});

const { t } = useI18n({ useScope: 'global' });
const { showError, showSuccess } = useToast();
const issue = useItemIssue();
// The SAME data layer the inbox hub renders from — not a second reading of the
// index. Its awaiting/history partition and its deliberate oldest-first
// `reverse()` ARE the ordering rule, so reusing it is what guarantees the "next"
// offered here is the row sitting at the top of that hub. Re-deriving either
// would be two orderings that can silently disagree.
const inbox = useDecisionsInbox();
// The club's roster, for the suggestion sheet's picker. Fetched when the sheet
// opens rather than on page load: most visits to this page are about reading an
// answer, and a member who never names a shop should never pay for the request.
const {
  suppliers,
  rewardCents,
  loading: suppliersLoading,
  failed: suppliersFailed,
  load: loadSuppliers,
} = useSupplierList();

const cardHost = ref(null);
// Her fourth reply's sheet. Page-level rather than inside IssueCard for the
// same reason the order sheets are hoisted: it must not live inside the answer
// stack it is offered from.
const suggestSheetOpen = ref(false);
// Her current pick on the answer stack at the moment she opened the fourth
// reply — `{ response, optionIndex }` or null (tips-wave P3-A, U-80). Naming a
// shop must not silently downgrade a YES/NO she has already made to a bare
// WAIT; captured here because IssueCard's `selection` is local and inert
// until submit (see that component's docblock).
const pendingAnswerSelection = ref(null);
// The next decision waiting on her, learned AFTER a successful send. Null means
// there is no link — nothing waiting, the list hasn't landed yet, or the fetch
// failed. All three render identically, which is the point.
const nextIssueId = ref(null);

const issueId = computed(() => props.f7route?.params?.issueId ?? null);
const isReadOnly = computed(() => issue.state.value === 'read-only');
const avatarUrl = computed(
  () => issue.orderBoxItem.value?.avatarUrl || issue.orderBoxItem.value?.productAvatarUrl || null,
);

// The whole item goes to the card: every money figure it derives needs qty,
// price AND discount together (see `optionLineImpact`). The nested item is NOT
// refreshed by a respond POST (`mergeIssue` keeps the prior one), so those
// figures are stable across a submit and only re-read on a real `refresh()` —
// which is what we want: the amount shown on the control is the amount that was
// true when the member decided.
//
// The confirmation line, interpolated here and handed DOWN to the card, which
// now renders it inline. `confirmKey` is null when there is genuinely nothing to
// confirm (no answer submitted, or an outcome that would be false on this item),
// so the empty string is a real state and the card gates on it.
const confirmMessage = computed(() =>
  issue.confirmKey.value ? t(issue.confirmKey.value, { item: issue.itemName.value }) : '',
);

// A withdrawal (`request-closed`) is resolved but not an outcome — it gets its
// own neutral tone/icon rather than borrowing the "done" success styling the
// other three terminals earn. Mirrors IssueTimeline's neutral-terminal step
// and IssueRow's neutral outcome badge — same distinction, three surfaces.
const readOnlyBannerClass = computed(() => {
  if (!issue.isResolvedIssue.value) return 'issue-decision__banner--handled';
  return issue.outcomeIsNeutral.value
    ? 'issue-decision__banner--neutral'
    : 'issue-decision__banner--done';
});
const readOnlyBannerIcon = computed(() => {
  if (!issue.isResolvedIssue.value) return 'clock_fill';
  return issue.outcomeIsNeutral.value ? 'info_circle_fill' : 'checkmark_seal_fill';
});

// role="group" + this label give the tabindex="-1" focus target below something
// to announce — without it, moving focus into a bare unlabeled div is silent for
// screen readers, defeating the point of the focus-management watcher. The pill
// label tracks the issue's actual state (the group is no longer always a pending
// decision), via the same key map TurnPill renders from.
const cardAriaLabel = computed(() => {
  const pill = t(pillLabelKey(issue.pillVariant.value));
  const item = issue.itemName.value;
  return item ? `${pill}: ${item}` : pill;
});

// Load on mount and whenever the id changes (a second deep-link into a fresh
// card). immediate covers the cold-boot / notification path.
watch(
  issueId,
  (id) => {
    nextIssueId.value = null;
    if (id != null) issue.load(id);
  },
  { immediate: true },
);

// Re-read on RE-entry (coming back from wherever the member went) so a leader's
// apply/defer/OOS and any new timeline steps appear — this surface has no
// realtime push. The FIRST beforein is skipped explicitly: it fires after mount,
// i.e. after the load() above already went out, and a flag is deterministic where
// checking "has the first fetch resolved yet" would be a race. The flag is
// instance-local and that is load-bearing for the chain: `reloadCurrent` mounts a
// NEW page component, so every issue in a chain gets its own flag and skips its
// own redundant first refresh. refreshIfIdle then refuses the two states a
// refresh would sabotage — an in-flight submit and the already-handled close.
// It no longer has a confirm end-state to protect: re-entering after answering
// DOES refresh now, which is what picks up the leader acting on her answer.
let firstBeforeIn = true;
function onBeforeIn() {
  if (firstBeforeIn) {
    firstBeforeIn = false;
    return;
  }
  issue.refreshIfIdle();
}

async function onPtr(done) {
  try {
    await issue.refreshIfIdle();
  } finally {
    if (typeof done === 'function') done();
  }
}

// The states from which arriving at a readable state is a genuine ARRIVAL worth
// moving focus for. Excludes the readable states themselves (a background refresh
// landing on the same state must not yank focus out from under the member) and
// 'submitting' (a refused submit should leave focus by the role="alert" message,
// not jump back to the top of the card).
const FOCUS_ENTRY_STATES = ['loading', 'error', undefined];

// Move focus into the card when it becomes readable (screen-reader + keyboard
// focus management — the decision, or the history, is what the member came for).
//
// `preventScroll` is not a nicety. What this call wants is the AT announcement
// and the keyboard entry point; scrolling is a side effect nobody asked for, and
// on Chromium it cost her the way out of the page:
//
//   focus() → the browser scrolls the card host into view (~100px, because the
//   host carries a translateY entry animation) → F7 reads that as a downward
//   scroll and retracts the navbar (`hideOnPageScroll` is on app-wide, App.vue)
//   → the back link sits at top:-60px, and this route hides the tabbar, so a
//   member who lands here and does not scroll has no visible way back.
//
// Traced on the real page: focus() at t, scroll(102px) at t+16ms, navbar-hidden
// at t+17ms. WebKit never scrolled here, which is why it only ever showed on
// Chromium. Ignored (so: today's behaviour) on iOS 15.0–15.3, which predates
// `preventScroll` — the browserslist floor. Nothing breaks there; it just
// scrolls as before.
watch(
  () => issue.state.value,
  async (s, prev) => {
    if ((s === 'awaiting' || s === 'read-only') && FOCUS_ENTRY_STATES.includes(prev)) {
      await nextTick();
      cardHost.value?.focus?.({ preventScroll: true });
    }
  },
);

function openSuggestSheet(payload) {
  // Only the LIVE-card path forwards a selection (see IssueCard's emit); the
  // read-only door's tip has no answer-stack pick to carry.
  pendingAnswerSelection.value = payload?.selection ?? null;
  suggestSheetOpen.value = true;
  // Fire-and-forget: the sheet renders its typed field immediately and the
  // picker appears if and when the roster lands. A failure is "no list", never
  // an error — see useSupplierList. A shop named inside a live question rides
  // her answer and records no suggestion, so there is nothing to tag or reward
  // there; only the read-only door's line-mode tip asks about the line's product.
  loadSuppliers(suggestMode.value === 'line' ? issue.orderBoxItem.value?.productId : null);
}

/**
 * The card's cancel button on a read-only, out-of-stock line (tips-wave P3-A).
 * Confirmed, because it is genuinely irreversible from where she stands — the
 * SAME confirm strings `ItemSheets.vue`'s own out-of-stock cancel uses, so one
 * action reads the same wherever she meets it.
 *
 * `dialog.create` with explicit buttons rather than `dialog.confirm`: the
 * shorthand renders English OK/Cancel regardless of locale.
 */
function confirmCancelOutOfStock() {
  if (issue.cancellingOutOfStock.value) return;

  f7.dialog
    .create({
      title: t('item.status.confirmations.cancelOutOfStockTitle'),
      text: t('item.status.confirmations.cancelOutOfStockMessage'),
      buttons: [
        { text: t('general.cancel'), close: true },
        { text: t('general.confirm'), close: true, onClick: runCancelOutOfStock },
      ],
      verticalButtons: false,
    })
    .open();
}

async function runCancelOutOfStock() {
  const { ok, message } = await issue.cancelOutOfStockItem();
  if (ok) {
    showSuccess(t('item.status.feedback.outOfStockCancelled'));
    return;
  }
  showError(message || t('item.status.errors.cannotCancel'));
}

/**
 * She named a shop instead of picking one of the three answers.
 *
 * ── IT IS AN ANSWER NOW, NOT A TIP (2026-09-15, owner answer H-G) ───────────
 * This used to POST the suggest-supplier door and then re-read the issue,
 * because the backend treated that call as BOTH — it recorded a supplier tip
 * AND recorded `wait` on the question. Two writes, one of which the member
 * never asked for, and a mandatory re-read to find out what had happened.
 *
 * `POST /item-issues/{id}/-actions/respond` now takes `payload.supplier_id`
 * directly, and a WAIT carrying a shop is legal even on a question that never
 * offered «استناني» — the one named bypass, which moved off the tip door for
 * exactly this. So the fourth reply goes through the SAME path as the other
 * three: one write, the composable's own revision and refusal handling, and no
 * re-read, because `respond` hands back the fresh issue.
 *
 * The note rides with it. `respond` keeps an omitted `supplier_id` rather than
 * clearing it, so revising her note later does not silently drop her shop.
 */
/**
 * TWO DOORS ONTO ONE SHEET, and the mode is the whole difference.
 *
 * On a LIVE card the shop is part of her ANSWER to a question the club is
 * waiting on — `answer` mode, posted through `respond`.
 *
 * On a READ-ONLY card there is no question left to answer: it is over, and the
 * door is the one thing she can still do about the LINE. That stays an ordinary
 * line tip through the suggest-supplier endpoint, which is also why it keeps
 * its own «لسه عارف حد عنده الصنف؟» label rather than the answer's.
 */
const suggestMode = computed(() => (isReadOnly.value ? 'line' : 'answer'));

/**
 * Naming a shop sends her CURRENT selection with it — a YES on an option, or a
 * NO — and falls back to WAIT only when she picked nothing (tips-wave P3-A,
 * U-80). Before this, tapping «أعرف مين عنده» after picking an option silently
 * downgraded that pick to WAIT the moment she named a shop, discarding a
 * choice she had already made.
 */
async function onSupplierPicked({ supplierId, note }) {
  suggestSheetOpen.value = false;
  const selection = pendingAnswerSelection.value;
  pendingAnswerSelection.value = null;

  const response = selection?.response ?? MEMBER_RESPONSE.WAIT;
  const optionIndex =
    response === MEMBER_RESPONSE.YES && Number.isInteger(selection?.optionIndex)
      ? selection.optionIndex
      : undefined;

  await onRespond({
    response,
    payload: { supplierId, note, noteCleared: note === '', optionIndex },
  });
}

/**
 * The read-only card's tip landed. Nothing about the QUESTION changed — it was
 * already over — but the line now carries her tip, so the card is re-read to
 * pick up the receipt and to drop the door (the line has spent its one tip).
 */
async function onSuggestionSubmitted() {
  showSuccess(t('item.suggestSupplier.success'));
  await issue.refresh();
}

/**
 * The line moved past suggesting while she was picking (403). Only reachable
 * from the READ-ONLY card's tip door — the answer path has no 403 of its own.
 */
async function onSuggestionStale({ message }) {
  showError(message);
  await issue.refresh();
}

async function onRespond({ response, payload }) {
  // A fresh send re-opens the question of what comes next, so the previous
  // answer's link goes before the POST rather than after it: it must never
  // survive next to a confirmation it was not fetched for.
  nextIssueId.value = null;
  const result = await issue.respond(response, payload);
  // `retry`: a 409 on a question that is still hers — the buttons stay live.
  if (result?.networkError || result?.retry) {
    showError(t('common.errorTryAgain'));
    return;
  }
  // Nothing landed (refused / lost the race to a leader-apply), or the card
  // flipped read-only in the same breath — either way there is no inline
  // confirmation on screen to append a link to, and `GET /item-issues` is
  // unpaginated, so this is a call worth not making.
  if (!issue.justSubmitted.value || isReadOnly.value) return;
  await loadNextDecision();
}

/**
 * What is she being asked next? Read off the inbox's OWN awaiting partition —
 * see the `inbox` note above for why that matters more than it looks.
 *
 * Nothing needs filtering against the issue she just answered: her answer moves
 * it to `open`, so it leaves the awaiting partition on its own. The one skip
 * here is not that filter — it is a self-navigation guard. If the server ever
 * hands back a list that still holds this issue (a status that did not move, a
 * proxy serving a stale read), offering it would put «في قرار تاني مستنيك» on a
 * link back to the card she is already looking at.
 *
 * `inbox.load()` swallows its own failures into `error`, so a dead fetch leaves
 * `rows` empty and this leaves `nextIssueId` null — no link, no error, nothing
 * for her to do about it.
 */
async function loadNextDecision() {
  await inbox.load();
  const current = issueId.value == null ? null : String(issueId.value);
  const next = inbox.rows.value.find((row) => row.issueId !== current);
  nextIssueId.value = next?.issueId ?? null;
}

/**
 * Onward to the next decision — `reloadCurrent`, which is load-bearing.
 *
 * Without it, five answers stack five issue pages and back-tapping walks her
 * backwards through cards she has already answered; with it the history length
 * never moves, so back always returns to wherever she entered the chain from.
 *
 * A real navigation, NOT re-pointing `useItemIssue` at the next id in place.
 * That would be cheaper and it would make the URL lie: `/issues/{old}/` would
 * survive a refresh, a back-tap and any re-share of the link.
 */
function goToNextDecision() {
  const id = nextIssueId.value;
  if (id == null) return;
  props.f7router?.navigate(issueRoute(id), { reloadCurrent: true });
}

/** Where "done" / back goes: the inbox — it re-scans and shows any remaining
 *  decisions or the warm empty state (works from every entry point). Pages
 *  navigate with their OWN injected router (correct for page-level history), but
 *  the path comes from useIssueNavigation so it is spelt in exactly one file. */
function goToInbox(clear = false) {
  props.f7router?.navigate(ISSUES_INBOX_ROUTE, { clearPreviousHistory: clear });
}

function done() {
  goToInbox(true);
}

function leave() {
  const r = props.f7router;
  if (r?.history && r.history.length > 1) r.back();
  else goToInbox(false);
}
</script>

<style scoped>
.issue-decision__wrap {
  max-width: 560px;
  margin: 0 auto;
  padding: 16px 14px 32px;
}

.issue-decision__card-host {
  outline: none;
  animation: issue-card-in 320ms var(--poolit-transition-ease, ease) both;
}

@keyframes issue-card-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .issue-decision__card-host {
    animation: issue-card-fade 180ms ease both;
  }
}

@keyframes issue-card-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Read-only banner — why there's nothing to do, above the detail it explains. */
.issue-decision__banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  padding: 13px 14px;
  border-radius: var(--poolit-radius-lg, 16px);
}

.issue-decision__banner--done {
  background: var(--ci-ok-bg, #e1f3e8);
  color: var(--ci-ok-fg, #16964f);
}

.issue-decision__banner--handled {
  background: var(--ci-brand-bg, #f0eaff);
  color: var(--ci-brand-fg, #6621e2);
}

.issue-decision__banner--neutral {
  background: var(--poolit-bg-secondary, #f5f7fa);
  color: var(--poolit-text-secondary, #3d4a44);
}

.issue-decision__banner-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.issue-decision__banner-body {
  flex: 1;
  min-width: 0;
}

.issue-decision__banner-title {
  margin: 0 0 4px;
  font-size: var(--poolit-font-size-body, 0.98rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.4;
}

.issue-decision__banner-msg {
  margin: 0;
  font-size: var(--poolit-font-size-small, 0.86rem);
  line-height: 1.65;
  color: var(--poolit-text-secondary, #3d4a44);
}

.issue-decision__banner-outcome {
  margin: 6px 0 0;
  font-size: var(--poolit-font-size-small, 0.86rem);
  font-weight: var(--poolit-font-weight-semibold, 600);
  line-height: 1.5;
}

/* The next decision, inside the card's confirmation. Text-styled and inheriting
   `currentColor` from the confirmation block, so it carries that block's already
   measured foreground/background pair rather than introducing a second one. The
   48px min-height is the bar every interactive target on this surface holds. */
.issue-decision__next {
  display: inline-flex;
  align-items: center;
  min-height: 48px;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font-family: inherit;
  font-size: var(--poolit-font-size-sm, 0.8125rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.5;
  text-align: start;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.issue-decision__next:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ci-brand-ring);
  border-radius: var(--poolit-radius-sm, 4px);
}

.issue-decision__back {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.issue-decision__back-label {
  font-size: var(--poolit-font-size-body, 0.95rem);
}

/* Skeleton */
.issue-skeleton {
  background: var(--ci-surface, #fff);
  border: 1px solid var(--ci-border, #dce4e0);
  border-radius: var(--poolit-radius-xl, 20px);
  padding: 18px 16px;
}

.issue-skeleton__head {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.issue-skeleton__thumb {
  width: 52px;
  height: 52px;
  border-radius: var(--poolit-radius-lg, 14px);
  --f7-skeleton-color: var(--ci-surface-soft, #eef2f0);
}

.issue-skeleton__ident {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.issue-skeleton__line {
  height: 14px;
  border-radius: 6px;
}

.issue-skeleton__line--name {
  width: 62%;
}

.issue-skeleton__line--pill {
  width: 38%;
  height: 20px;
  border-radius: 999px;
}

.issue-skeleton__situation {
  height: 68px;
  border-radius: var(--poolit-radius-lg, 14px);
  margin-bottom: 16px;
}

.issue-skeleton__body {
  height: 120px;
  border-radius: var(--poolit-radius-lg, 14px);
  margin-bottom: 16px;
}

.issue-skeleton__btn {
  height: 48px;
  border-radius: var(--poolit-radius-lg, 12px);
  margin-bottom: 10px;
}

.issue-skeleton__btn--ghost {
  width: 70%;
  margin-inline: auto;
}

/* Error */
.issue-decision__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 48px 20px;
}

.issue-decision__error-msg {
  margin: 0;
  color: var(--poolit-text-secondary, #3d4a44);
  font-size: var(--poolit-font-size-body, 0.95rem);
  line-height: 1.6;
}

.issue-decision__retry {
  margin-top: 4px;
  min-width: 180px;
}
</style>
